import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Pencil, Send, Monitor, Smartphone, Inbox, CalendarClock } from "lucide-react";
import { API, authHeaders } from "./shared";
import BestSendTimeHint from "./BestSendTimeHint";

const ScheduleControls = ({ subject, message, campaignName, onScheduled }) => {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState("");
  const [recommended, setRecommended] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/best-send-time`, authHeaders())
      .then((r) => { setRecommended(r.data.next_occurrence || ""); setWhen(r.data.next_occurrence || ""); })
      .catch(() => {});
  }, []);

  const confirmSchedule = async () => {
    if (!when) { toast.error("Pick a date and time first"); return; }
    const local = new Date(when);
    if (!window.confirm(`Schedule this campaign to send on ${local.toLocaleString()}?\n\nIt will NOT send without this approval.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/scheduled-campaigns`, {
        subject, message, name: campaignName || null, scheduled_for: local.toISOString(),
      }, authHeaders());
      toast.success(`Campaign scheduled for ${local.toLocaleString()} — you can cancel or reschedule anytime in the Campaigns tab`);
      onScheduled && onScheduled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="preview-schedule-toggle" onClick={() => setOpen(true)} className="flex items-center gap-1.5 px-4 py-2 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold/10 transition-colors">
        <CalendarClock size={12} /> Schedule…
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="schedule-controls">
      <input
        data-testid="schedule-datetime-input"
        type="datetime-local"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
        className="bg-brand-bg border border-brand-border text-brand-text font-sans text-xs px-2 py-1.5 focus:border-brand-gold outline-none"
      />
      {recommended && (
        <button data-testid="schedule-use-recommended" onClick={() => setWhen(recommended)} className="font-sans text-[11px] px-2.5 py-1.5 border border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold transition-colors">
          Use recommended
        </button>
      )}
      <button data-testid="schedule-confirm-btn" onClick={confirmSchedule} disabled={busy} className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50">
        <CalendarClock size={12} /> {busy ? "Scheduling…" : "Confirm Schedule"}
      </button>
      <button onClick={() => setOpen(false)} className="font-sans text-[11px] px-2 py-1.5 text-brand-text-muted hover:text-brand-text">back</button>
    </div>
  );
};

const BlastPreviewModal = ({ subject, message, campaignName, onApprove, onEdit, onClose, onScheduled, approving }) => {
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("desktop");
  const [testing, setTesting] = useState(false);
  const [testSent, setTestSent] = useState(false);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/shop/blast/test-send`, { subject, message }, authHeaders());
      setTestSent(true);
      toast.success(`Test sent to your inbox (${r.data.to.join(", ")}). Customer campaign is still paused.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setTesting(false); }
  };

  useEffect(() => {
    axios.post(`${API}/admin/shop/blast/preview`, { subject, message }, authHeaders())
      .then((r) => setPreview(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Failed to build preview");
        onClose();
      });
  }, [subject, message, onClose]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" data-testid="blast-preview-modal">
      <div className="bg-brand-surface border border-brand-gold/50 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-brand-border">
          <h3 className="font-serif text-lg text-brand-text">Email Preview — exactly what clients will see</h3>
          <button data-testid="preview-close-btn" onClick={onClose} className="p-1.5 text-brand-text-muted hover:text-brand-text"><X size={16} /></button>
        </div>

        {!preview ? (
          <p className="font-sans text-sm text-brand-text-muted p-8 text-center">Building preview…</p>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-brand-border/50 space-y-1">
              <p className="font-sans text-xs text-brand-text-muted">From: <span className="text-brand-text" data-testid="preview-sender">{preview.from}</span></p>
              <p className="font-sans text-xs text-brand-text-muted">Subject: <span className="text-brand-gold font-semibold" data-testid="preview-subject">{preview.subject}</span></p>
              {preview.products_count === 0 && (
                <p className="font-sans text-[11px] text-amber-400">Note: no approved products yet — the product row will be empty until you approve products in the Suppliers tab.</p>
              )}
            </div>
            <div className="flex items-center gap-2 px-4 py-2 border-b border-brand-border/50">
              <button data-testid="preview-desktop-btn" onClick={() => setMode("desktop")} className={`flex items-center gap-1.5 font-sans text-[11px] px-3 py-1 border transition-colors ${mode === "desktop" ? "border-brand-gold text-brand-gold bg-brand-gold/10" : "border-brand-border text-brand-text-muted"}`}>
                <Monitor size={12} /> Desktop
              </button>
              <button data-testid="preview-mobile-btn" onClick={() => setMode("mobile")} className={`flex items-center gap-1.5 font-sans text-[11px] px-3 py-1 border transition-colors ${mode === "mobile" ? "border-brand-gold text-brand-gold bg-brand-gold/10" : "border-brand-border text-brand-text-muted"}`}>
                <Smartphone size={12} /> Mobile
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-black/40 p-4 flex justify-center">
              <iframe
                title="Blast email preview"
                data-testid="preview-iframe"
                srcDoc={`<body style="margin:0;background:#000;">${preview.html}</body>`}
                sandbox=""
                style={{ width: mode === "mobile" ? 375 : 620, height: 560, border: "1px solid #2C241D", background: "#000" }}
              />
            </div>
          </>
        )}

        <div className="flex flex-wrap gap-3 p-4 border-t border-brand-border items-center">
          <div className="w-full -mb-2"><BestSendTimeHint /></div>
          <button data-testid="preview-edit-btn" onClick={onEdit} className="flex items-center gap-1.5 px-4 py-2 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold hover:text-brand-gold transition-colors">
            <Pencil size={12} /> Edit
          </button>
          <button
            data-testid="preview-test-send-btn"
            onClick={sendTest}
            disabled={!preview || testing}
            className="flex items-center gap-1.5 px-4 py-2 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold/10 transition-colors disabled:opacity-50"
          >
            <Inbox size={12} /> {testing ? "Sending test…" : testSent ? "Send Test Again" : "Send Test to Me"}
          </button>
          <button
            data-testid="preview-approve-btn"
            onClick={onApprove}
            disabled={!preview || approving}
            className="flex items-center gap-1.5 px-5 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50"
          >
            <Send size={12} /> {approving ? "Sending…" : "Approve & Send Now"}
          </button>
          <ScheduleControls subject={subject} message={message} campaignName={campaignName} onScheduled={onScheduled || onClose} />
          <button data-testid="preview-cancel-btn" onClick={onClose} className="ml-auto px-4 py-2 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold transition-colors">
            Cancel
          </button>
          {testSent && (
            <p data-testid="preview-test-note" className="w-full font-sans text-[11px] text-green-400 mt-1">
              Test delivered to your inbox — the customer campaign stays paused until you click Approve &amp; Send.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlastPreviewModal;
